import { Component, DestroyRef, OnInit, Type, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService, MenuItem, SortEvent } from 'primeng/api';
import { TableModule, TablePageEvent } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { MenuModule } from 'primeng/menu';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { AuditLogShowDetailsDialogComponent } from '@client/app/feature-modules/admin/modules/admin-audit-log/components/audit-log-show-details-dialog/audit-log-show-details-dialog.component';
import { AuditLogViewChangesDialogComponent } from '@client/app/feature-modules/admin/modules/admin-audit-log/components/audit-log-view-changes-dialog/audit-log-view-changes-dialog.component';
import { AuditLogUndoReviewCheckDialogComponent } from '@client/app/feature-modules/admin/modules/admin-audit-log/components/audit-log-undo-review-check-dialog/audit-log-undo-review-check-dialog.component';
import { UnderscorelessPipe } from '@client/app/feature-modules/admin/modules/admin-audit-log/pipes/underscoreless.pipe';
import { activeFeatures } from '@monsido/core/constants/general.constant';
import { MonUIRouterTransitionService } from '@monsido/services/ui-router-transition-service/ui-router-transition-service';
import { StateService } from '@uirouter/core';
import { AuditRepoService, AuditRepoRequestType } from '@client/app/services/api/audit-repo/audit-repo.service';
import { AuditlogModulesService } from '@client/app/services/auditlog-modules/auditlog-modules.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { ExporterService } from '@client/app/modules/export/services/exporter.service';
import { TotalDomainsService } from '@client/app/services/total-domains/total-domains.service';
import { ParamService } from '@monsido/core/param/param.service';
import { MonModalService } from '@client/app/modules/modals/services/mon-modal/mon-modal.service';
import { AuditlogService } from '@client/app/pages/admin/auditlog/auditlog.component.service';
import { UserService } from '@client/app/services/entities/user/user/user.service';
import { Domain } from '@client/ng2/modules/models/api/domain';
import { User } from '@client/ng2/modules/models/api/user';
import { AuditLogModel } from '@client/app/models/audit-log.model';
import { AVAILABLE_PAGE_SIZE } from '@client/app/shared-modules/api/constants/page-size.constant';
import { SetToMyTimezonePipe } from '@client/app/pipe/set-to-my-timezone/set-to-my-timezone.pipe';
import { UserDatePipe } from '@client/app/pipe/user-date/user-date.pipe';
import { MonTableCollection } from '@monsido/ng2/models/table-collection.interface';
import { Params } from '@angular/router';

import { DialogService, DynamicDialogModule, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ActionTriggerService } from '@client/app/shared-modules/tracking/action-trigger/action-trigger.service';
import { MyExportsDialogService } from '@client/app/shared-modules/my-exports/services/my-exports-dialog.service';
import { CreateExportDialogService } from '@client/app/shared-modules/create-export/services/create-export-dialog.service';
import { CreateExport } from '@client/app/shared-modules/create-export/types/create-export.type';
import { AdminLayoutService } from '@client/app/feature-modules/admin/services/admin-layout.service';
import { MonFoldableDirective } from '@client/app/feature-modules/shared/directives/mon-foldable/mon-foldable.directive';
import { PageLayoutComponent } from '@client/app/feature-modules/shared/components/page-layout/page-layout.component';
import { TableWrapperComponent } from '@client/app/shared-modules/table/components/table-wrapper.component';
import { TableCellTemplateDirective } from '@client/app/shared-modules/table/directives/table-cell-template.directive';

interface AuditLogFilterOption {
    name: string;
    value: string | number;
}

type ComputedAuditLog = AuditLogModel & {
    actionItems: MenuItem[];
};

@Component({
    selector: 'mon-admin-audit-log',
    templateUrl: './audit-log.component.html',
    styleUrls: ['./audit-log.component.scss'],
    standalone: true,
    imports: [
        TranslocoModule,
        ConfirmDialog,
        TableModule,
        TooltipModule,
        CommonModule,
        FormsModule,
        ButtonModule,
        MenuModule,
        SelectModule,
        UnderscorelessPipe,
        UserDatePipe,
        DatePickerModule,
        DynamicDialogModule,
        MonFoldableDirective,
        PageLayoutComponent,
        TableWrapperComponent,
        TableCellTemplateDirective,
    ],
    providers: [
        ConfirmationService,
        MessageService,
        MonUIRouterTransitionService,
        AuditRepoService,
        AuditlogModulesService,
        MonEventService,
        ExporterService,
        TotalDomainsService,
        ParamService,
        MonModalService,
        AuditlogService,
        UserService,
        SetToMyTimezonePipe,
        DialogService,
        MyExportsDialogService,
        CreateExportDialogService,
    ],
})
export class AdminAuditLogComponent implements OnInit {
    private readonly translocoService = inject(TranslocoService);

    readonly AVAILABLE_PAGE_SIZE = AVAILABLE_PAGE_SIZE;

    readonly TABLE_SCHEMA = TableWrapperComponent.createTableSchema<ComputedAuditLog>({
        DATE: {
            label: this.translocoService.translate('adminAuditLog.table.header.date'),
            id: 'created_at',
            type: 'custom',
            sortable: true,
        },
        USER: {
            label: this.translocoService.translate('adminAuditLog.table.header.user'),
            id: 'author',
            type: 'property',
            sortable: true,
        },
        DOMAIN: {
            label: this.translocoService.translate('adminAuditLog.table.header.domain'),
            id: 'domain',
            type: 'custom',
        },
        MODULE: {
            label: this.translocoService.translate('adminAuditLog.table.header.module'),
            id: 'module',
            type: 'property',
        },
        TARGET: {
            label: this.translocoService.translate('adminAuditLog.table.header.target'),
            id: 'target_uri',
            type: 'property',
        },
        IDENTIFIER: {
            label: this.translocoService.translate('adminAuditLog.table.header.identifier'),
            id: 'identifier',
            type: 'custom',
        },
        ACTION: {
            label: this.translocoService.translate('adminAuditLog.table.header.action'),
            id: 'action',
            type: 'custom',
        },
    });

    domains: AuditLogFilterOption[] = [];
    users: AuditLogFilterOption[] = [];
    modules: AuditLogFilterOption[] = [];
    logs: ComputedAuditLog[] = [];
    domainsLoading: boolean = true;
    date: { endDate: Date | null, startDate: Date | null } = { endDate: null, startDate: null };
    dateRange: [Date | null, Date | null] | null = null;
    domainIdModel?: AuditLogFilterOption | undefined;
    userIdModel?: AuditLogFilterOption | undefined;
    moduleModel?: AuditLogFilterOption | undefined;
    module: string | number = 'all';
    domain_id: string | number = 'all';
    user_id: string | number = 'all';
    page: number = 1;
    pageSize: number = 10;
    loading: boolean = true;
    currentPage: number = 1;
    perPage: number = 10;
    totalRecords: number = 0;
    maxDate: Date = new Date;
    sortBy: string | undefined = '';
    sortDir: string | null = null;

    private menuTriggerElement: HTMLElement | null = null;

    constructor (
        private readonly monUIRouterTransitionService: MonUIRouterTransitionService,
        private readonly stateService: StateService,
        private readonly auditRepoService: AuditRepoService,
        private readonly auditlogModulesService: AuditlogModulesService,
        private readonly totalDomainsService: TotalDomainsService,
        private readonly paramService: ParamService,
        private readonly auditlogService: AuditlogService,
        private readonly userService: UserService,
        private readonly destroyRef: DestroyRef,
        private readonly setToMyTimezonePipe: SetToMyTimezonePipe,
        private readonly actionTriggerService: ActionTriggerService,
        private readonly createExportDialogService: CreateExportDialogService,
        private readonly dialogService: DialogService,
        private readonly adminLayoutService: AdminLayoutService,
    ) {
        this.maxDate.setHours(23, 59, 59, 999);
    }

    ngOnInit (): void {
        this.setBreadcrumbs();
        if (!activeFeatures.audit_log) {
            this.monUIRouterTransitionService.onTransitionFinished(() => {
                this.stateService.go('base.dashboard', {}, { location: 'replace' });
            });
            return;
        }

        this.loadLocations();
        this.setupModules();
        this.setupDomains();
        this.setupUsers();
        this.getPages();

        const deregisterHook = this.paramService.subscribeParamsChangeOnCurrentState((params) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            this.page = params.page ?? 1;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            this.pageSize = params.page_size ?? 10;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            this.module = params.module ?? 'all';
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            this.domain_id = params.domain_id ?? 'all';
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            this.user_id = params.user_id ?? 'all';
            this.date.startDate = params.from ? new Date(params.from) : null;
            this.date.endDate = params.to ? new Date(params.to) : null;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            this.sortBy = params.sort_by ?? null;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            this.sortDir = params.sort_dir ?? null;

            this.getPages();
        });

        this.destroyRef.onDestroy(() => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            deregisterHook();
        });
    }

    public onMenuHide (): void {
        const el = document.activeElement;
        this.menuTriggerElement = (el instanceof HTMLElement) ? el : null;
    }

    setBreadcrumbs (): void {
        this.adminLayoutService.setBreadcrumbs({
            uiStates: [
                {
                    label: this.translocoService.translate('admin.auditLog.table.breadcrumbLabel'),
                    state: 'base.customer.admin.audit-log',
                },
            ],
        });
    }

    loadLocations () {
        const params = this.paramService.getParams();

        this.module = params['module'] || 'all';
        this.domain_id = params['domain_id'] || 'all';
        this.user_id = params['user_id'] || 'all';
        this.sortBy = params['sort_by'];
        this.sortDir = params['sort_dir'] || null;
        this.pageSize = params['page_size'] ? parseInt(params['page_size'], 10) : 10;
        this.page = params['page'] ? parseInt(params['page'], 10) : 1;

        const from = params['from'];
        const to = params['to'];

        if (to) {
            this.date.endDate = new Date(to);
            this.date.endDate.setHours(23,59,59,999);
        } else {
            this.date.endDate = null;
        }
        if (from) {
            this.date.startDate = new Date(from);
            this.date.startDate.setHours(0,0,0,0);
        } else {
            this.date.startDate = null;
        }
        if (this.date.startDate && this.date.endDate) {
            this.dateRange = [this.date.startDate, this.date.endDate];
        }
    }

    updateDomainId () {
        if (this.domainIdModel?.value) {
            this.domain_id = this.domainIdModel.value;
        } else {
            // Reset to 'all' when cleared
            this.domain_id = 'all';
        }
    }

    onDomainChange (option: AuditLogFilterOption | null) {
        this.domainIdModel = option ?? undefined;
        this.domain_id = option?.value ?? 'all';
    }

    updateUserId () {
        if (this.userIdModel?.value) {
            this.user_id = this.userIdModel.value;
        } else {
            // Reset to 'all' when cleared
            this.user_id = 'all';
        }
    }

    updateModule () {
        if (this.moduleModel?.value) {
            this.module = this.moduleModel.value;
        } else {
            // Reset to 'all' when cleared
            this.module = 'all';
        }
    }

    onDateRangeSelect () {
        if (Array.isArray(this.dateRange) && this.dateRange.length === 2) {
            const fromDate = this.dateRange[0] ? new Date(this.dateRange[0]) : null;
            const toDate = this.dateRange[1] ? new Date(this.dateRange[1]) : null;
            if (fromDate) {
                fromDate.setHours(0, 0, 0, 0);
            }
            if (toDate) {
                toDate.setHours(23, 59, 59, 999);
            }
            this.date.startDate = fromDate;
            this.date.endDate = toDate;
        } else {
            this.date.startDate = null;
            this.date.endDate = null;
        }
    }

    applyFilters () {
        this.updateDomainId();
        this.updateUserId();
        this.updateModule();
        this.onDateRangeSelect();
        this.page = 1;

        this.getPage();
    }

    sortLogs (event: SortEvent) {
        const { field, order } = event;
        const sortDir = order === 1 ? 'asc' : 'desc';
        this.sortBy = field;
        this.sortDir = sortDir;
        // back to first page on sorting
        this.page = 1;
        this.getPage();
    }

    getParams () {
        const params: AuditRepoRequestType = {};
        if (this.module !== 'all') {
            params.module = this.module;
        }

        if (this.domain_id !== 'all') {
            params.domain_id = Number(this.domain_id);
        }

        if (this.user_id !== 'all') {
            params.user_id = Number(this.user_id);
        }

        params.page = this.page;
        params.page_size = this.pageSize;

        if (this.sortBy && this.sortDir) {
            params.sort_by = this.sortBy;
            params.sort_dir = this.sortDir;
        }
        if (this.date.startDate && this.date.endDate) {
            params.from = this.date.startDate.toISOString();
            params.to = this.date.endDate.toISOString();
        }
        return params;
    }

    getPage () {
        const params: Params = {
            page: this.page,
            page_size: this.pageSize,
            module: this.module,
            domain_id: (!isNaN(Number(this.domain_id)) && this.domain_id !== 'all') ? Number(this.domain_id) : 'all',
            user_id: (!isNaN(Number(this.user_id)) && this.user_id !== 'all') ? Number(this.user_id) : 'all',
        };

        if (this.sortBy && this.sortDir) {
            params.sort_by = this.sortBy;
            params.sort_dir = this.sortDir;
        }
        if (this.date.startDate && this.date.endDate) {
            params.from = this.date.startDate.toISOString();
            params.to = this.date.endDate.toISOString();
        } else {
            params.from = null;
            params.to = null;
        }
        this.paramService.setParams(params);

    }

    getPages () {
        this.loading = true;
        this.auditRepoService.getAuditLogs(this.getParams()).then((data: MonTableCollection<AuditLogModel>) => {
            this.perPage = data.perPage ?? 0;
            this.currentPage = data.currentPage ?? 1;
            this.totalRecords = data.total ?? 0;

            this.logs = data.map((log: AuditLogModel) => {
                log.module = this.getModuleNameFromValue(log.module);
                log.target_uri = this.parseTargetURI(log.target_uri);
                log.created_at = this.setToMyTimezonePipe.transform(log.created_at).toDateString();

                const showDetails: MenuItem = {
                    label: this.translocoService.translate('adminAuditLog.table.action.showDetails'),
                    icon: 'pi pi-info-circle',
                    command: () => this.showDetails(log),
                };
                const viewChanges: MenuItem = {
                    label: this.translocoService.translate('adminAuditLog.table.action.viewChanges'),
                    icon: 'pi pi-eye',
                    command: () => this.viewChanges(log),
                };
                const undoCheckReview: MenuItem = {
                    label: this.translocoService.translate('adminAuditLog.table.action.undoCheckReview'),
                    icon: 'pi pi-undo',
                    command: () => this.undoCheckReview(log),
                };

                const computedLog: ComputedAuditLog = {
                    ...log,
                    actionItems: this.getActionMenuForLog(log, showDetails, viewChanges, undoCheckReview),
                };

                return computedLog;
            });
        }).finally(() => {
            this.loading = false;

        });
    }

    private getActionMenuForLog (
        log: AuditLogModel,
        showDetails: MenuItem,
        viewChanges: MenuItem,
        undoCheckReview: MenuItem,
    ): MenuItem[] {
        if (log.data?.changes && !this.isBlackListed(log.data.changes) && log.action !== 'delete') {
            return viewChanges ? [viewChanges] : [];
        }
        if (log.data?.source_code && !this.isAnUndoLog(log.action) && this.canUndo(log.apiTarget)) {
            return showDetails && undoCheckReview ? [showDetails, undoCheckReview] : [];
        }
        if ((log.data && (log.data.source_code || log.data.check_id) && !this.canUndo(log.apiTarget)) || this.isAnUndoLog(log.action)) {
            return showDetails ? [showDetails] : [];
        }
        return [];
    }

    showDetails (log: AuditLogModel) {
        const dialogRef = this.dialogWrapper(AuditLogShowDetailsDialogComponent,
            {
                log_data: log.data,
                action: log.action,
                target: log.apiTarget,
                source_code_id: log.apiTargetId,
                domain_id: (log.domain as { id: string | number }).id,
                callback: () => this.getPages(),
            },
            this.translocoService.translate('adminAuditLog.dialog.showDetails.title'),
        );

        dialogRef.onClose.subscribe(() => {
            this.menuTriggerElement?.focus();
            this.menuTriggerElement = null;
        });
    }

    viewChanges (log: AuditLogModel) {
        const dialogRef = this.dialogWrapper(AuditLogViewChangesDialogComponent,
            {
                log_data: log.data,
                created: ['created', 'create'].indexOf(log.action) !== -1,
            },
            this.translocoService.translate('adminAuditLog.dialog.viewChanges.title'),
        );

        dialogRef.onClose.subscribe(() => {
            this.menuTriggerElement?.focus();
            this.menuTriggerElement = null;
        });
    }

    undoCheckReview (log: AuditLogModel) {
        const dialogRef = this.dialogWrapper(AuditLogUndoReviewCheckDialogComponent,
            {
                domain_id: (log.domain as { id: string | number }).id,
                source_code_id: log.apiTargetId,
                check_id: log.data.check_id,
            },
            this.translocoService.translate('adminAuditLog.dialog.undoCheckReview.title'),
        );

        dialogRef.onClose.subscribe((result) => {
            this.menuTriggerElement?.focus();
            this.menuTriggerElement = null;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (result?.success) {
                this.getPages();
            }
        });
    }

    parseTargetURI (target: string) {
        const temp = target.substring(0, target.indexOf(':'));
        if (temp === 'PageCorrect') {
            return temp;
        }
        return temp.replace(/([A-Z])/g, ' $1').trim();
    }

    prepareDomains (domains: Domain[]) {
        if (domains.length) {
            const domainsArr: AuditLogFilterOption[] = domains.map(domain => ({
                name: domain.title,
                value: domain.id.toString(),
            }));
            let domainIdStr: string;
            if (this.domain_id) {
                domainIdStr = this.domain_id.toString();
            }
            this.domainIdModel = domainsArr.find(domain => domain.value === domainIdStr) ?? undefined;
            this.domains = domainsArr;
        }
        this.domainsLoading = false;
    }

    setupDomains () {
        this.totalDomainsService.requestAllDomains({
            params: {
                page: 0,
                page_size: 100,
            },
            domainCollection: [],
            isRecursive: false,
        });
        this.totalDomainsService.domainsEssential.subscribe((domains: Domain[] | null) => {
            if (domains) {
                this.prepareDomains(domains);
            }
        });
    }

    async setupUsers () {
        const users = await this.userService.tryGetUsersFromCache({ page: 1, page_size: 50 });
        Object.values(users).forEach((user: User) => {
            this.users.push({ name: user.email, value: user.id });
        });
        this.userIdModel = this.users.find(user => String(user.value) === String(this.user_id)) ?? undefined;
    }

    setupModules () {
        this.modules.push(...this.auditlogModulesService.getList());
        this.moduleModel = this.modules.find(module => module.value === this.module) ?? undefined;
    }

    onPageChange (pageChangeEvent: TablePageEvent) {
        const { first, rows } = pageChangeEvent;

        // Check if page size has changed
        const pageSizeChanged = rows !== this.perPage;

        // If page size changed, reset to page 1; otherwise calculate current page
        const pageNumber = pageSizeChanged ? 1 : Math.floor((first || 0) / (rows || 1)) + 1;

        this.paramService.updateParams({
            page: pageNumber,
            page_size: rows,
        });
    }

    isBlackListed (changes: Record<string, string[]>) {
        const attrs = Object.getOwnPropertyNames(changes);
        return attrs.indexOf('updated_at') !== -1 && attrs.length === 1;
    }

    getModuleNameFromValue (value: string) {
        return this.auditlogModulesService.getModuleNameFromValue(value);
    }

    isAnUndoLog (action: string) {
        return this.auditlogService.isAnUndoLog(action);
    }

    canUndo (target?: string) {
        return this.auditlogService.canUndo(target);
    }

    openCreateExport (): void {
        this.actionTriggerService.track({ action: 'admin-audit-log-create-export-btn' });

        const allParams = this.getParams();
        const { user_id, domain_id, module, from, to } = allParams;
        const exportParams = Object.fromEntries(
            Object.entries({ user_id, domain_id, module, from, to })
                .filter((entry) => entry[1] !== undefined),
        );

        const config: CreateExport = {
            buttonLabel: this.translocoService.translate('adminAuditLog.createExportDialog.buttonLabel'),
            payload: {
                category: 'audit_logs',
                params: exportParams,
            },
        };

        this.createExportDialogService.open(config);
    }

    dialogWrapper<T> (dialogComponent: Type<T>, data: Record<string, unknown>, header: string): DynamicDialogRef {
        return this.dialogService.open(dialogComponent, {
            data: data,
            header: header,
            baseZIndex: 10000,
            showHeader: true,
            closable: true,
            dismissableMask: true,
            width: '800px',
            modal: true,
        });
    }
}
